import { ClockIcon, LayersIcon, SquareCheckBigIcon } from 'lucide-react';
import React from 'react';
import { CancellableSearchBox } from '../../../components/search/cancellable-search-box.tsx';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../../components/ui/select.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { BillingStatus, TargetUnit, mapToTargetUnit } from '../../../server/utils/explorer-utils.ts';
import { useAppConfig } from '../../acl/acl-provider.tsx';
import { getSearchStringFieldForTab, useExplorerView } from '../hooks/use-explorer-query-params';
import { PresetDatePicker, PresetDatePickerWrapper } from './preset-data-picker';

export const ExplorerFilters = () => {
    const [visibilityModalOpen, setvisibilityModalOpen] = React.useState(false);
    const { t } = useLocale('explorer');
    const { canReadOrderSum } = useAppConfig();

    const { state, setState } = useExplorerView();

    const {
        searchString,
        tab: selectedTab,
        window: selectedWindow,
        customDateRange: selectedCustomDateRange = null,
        billing: selectedBillingStatus,
        sorting: selectedSortingFilter,
        targetUnit: selectedTargetUnit,
        yearPeriods: selectedYearPeriod,
    } = state;

    const setSelectedTargetUnit = (unit: TargetUnit) => {
        setState((prevState) => ({
            ...prevState,
            targetUnit: unit,
            sorting: {
                direction: selectedSortingFilter.direction,
                field: mapToTargetUnit[unit][selectedSortingFilter.field],
            },
        }));
    };
    return (
        <PresetDatePickerWrapper open={visibilityModalOpen} setOpen={setvisibilityModalOpen}>
            <div className="flex flex-row gap-2 scrollbar-none overflow-auto">
                <CancellableSearchBox
                    key={selectedTab}
                    placeholder={t('search_placeholder__' + selectedTab)}
                    value={searchString}
                    onChange={(value) => {
                        const tabSearchField = getSearchStringFieldForTab(selectedTab);
                        setState((prevState) => ({
                            ...prevState,
                            [tabSearchField]: value,
                        }));
                    }}
                    className="w-[210px] shrink-0"
                />

                <div className="flex">
                    <Select value={selectedTargetUnit} onValueChange={setSelectedTargetUnit}>
                        <SelectTrigger icon={LayersIcon} className="text-left">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                            {[TargetUnit.REVENUE, TargetUnit.UNITS].map((unit) => (
                                <SelectItem key={unit} value={unit.toString()}>
                                    {t(`radio_option__${unit}`)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="flex">
                    <Select
                        value={selectedBillingStatus}
                        onValueChange={(value) => {
                            setState((prevState) => ({
                                ...prevState,
                                billing: value as BillingStatus,
                            }));
                        }}
                    >
                        <SelectTrigger icon={SquareCheckBigIcon} className="h-8 text-left">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                            <SelectItem key={BillingStatus.BILLED} value={BillingStatus.BILLED}>
                                {t('filter_dropdown_status__billed')}
                            </SelectItem>
                            <SelectItem
                                key={BillingStatus.BILLED_WO_CREDIT_NOTE}
                                value={BillingStatus.BILLED_WO_CREDIT_NOTE}
                            >
                                {t('filter_dropdown_status__billed_wo_credit_note')}
                            </SelectItem>
                            <SelectItem key={BillingStatus.NOT_BILLED} value={BillingStatus.NOT_BILLED}>
                                {t('filter_dropdown_status__unbilled')}
                            </SelectItem>
                            {canReadOrderSum && (
                                <SelectItem key={BillingStatus.ORDER_SUM} value={BillingStatus.ORDER_SUM}>
                                    {t('filter_dropdown_status__order_sum')}
                                </SelectItem>
                            )}
                        </SelectContent>
                    </Select>
                </div>

                <div className="flex shrink-0">
                    <PresetDatePicker
                        open={visibilityModalOpen}
                        setOpen={setvisibilityModalOpen}
                        value={{
                            preset: selectedWindow,
                            dateRange: selectedCustomDateRange,
                        }}
                        onChange={({ preset, dateRange }) => {
                            setState((prevState) => ({
                                ...prevState,
                                window: preset,
                                customDateRange: dateRange
                                    ? {
                                          from: dateRange.from,
                                          to: dateRange.to,
                                      }
                                    : undefined,
                            }));
                        }}
                    />
                </div>

                <div className="flex">
                    <Select
                        value={selectedYearPeriod.toString()}
                        onValueChange={(value) => {
                            setState((prevState) => ({
                                ...prevState,
                                yearPeriods: value ? parseInt(value, 10) : 2,
                            }));
                        }}
                    >
                        <SelectTrigger icon={ClockIcon} className=" text-left">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                            {[2, 3, 4, 5].map((years) => (
                                <SelectItem key={years} value={years.toString()}>
                                    {t('filter_dropdown__period', { years })}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>
        </PresetDatePickerWrapper>
    );
};
